import type { ReactNode } from 'react'

type LozengeVariant = 'g' | 'o' | 'p' | 'b' | 'n' | 't'

/**
 * `span.lz` -> `lozenge` (process/08-COMPONENT-CATALOGUE.md -- `b`/`g`/`p`/`t` catalogued; `o`/`n`
 * are declared in `Prototype_v2.html`'s own `.lz` CSS but unused in its current markup, kept here
 * since nothing else names them). Six colour-coded inline markers named by what value they wrap
 * (`g`reen, `o`range, `p`eriod/cadence, `b`lue, `n`eutral/slate, `t`ype) -- never a status, which is
 * `Chip`'s job. Base shape (9px/800/uppercase/3px radius/2px 5px padding/white text) is the
 * prototype's own `.lz`; only `t` overrides colour to a light background with dark text.
 *
 * Five of six variants fold onto an existing token (process/11-TOKEN-MAP.md: #5243aa -> --accent-indigo,
 * #e3f3f8 -> --info-bg, #216e4e -> --accent-green, #e06c00 -> --orange, #0052cc -> --blue). `t`'s
 * own text colour, #227d9b, mints a new one, --lozenge-type-tx -- the map had it folded onto
 * --accent-teal (#00a3bf, `launcher-card`'s teal accent), a real but different colour at delta-36
 * in RGB, caught by `semantic-diff.mjs`'s own colour comparison the first time this variant was
 * drawn against it -- the same class of drift the map's own --chip-active-bg row was corrected
 * for at W172 (two colours sharing one token name).
 *
 * Extracted from six call sites hand-rolled ad hoc in `VSetgeneral.tsx` (each at `font-bold`, one
 * weight step light of the prototype's own 800) and a seventh, `v-health`'s own Object type
 * column, which rendered no lozenge at all -- plain text where the prototype draws `span.lz.t`.
 */
export function Lozenge({ variant, children }: { variant: LozengeVariant; children: ReactNode }) {
  const byVariant: Record<LozengeVariant, string> = {
    g: 'bg-accent-green text-white',
    o: 'bg-orange text-white',
    p: 'bg-accent-indigo text-white',
    b: 'bg-blue text-white',
    n: 'bg-slate text-white',
    t: 'bg-info-bg text-lozenge-type-tx',
  }
  return (
    <span data-cmp="lozenge" className={`inline-block text-micro font-heavy tracking-wide uppercase rounded-1 px-2 py-1 align-middle mx-1 ${byVariant[variant]}`}>
      {children}
    </span>
  )
}
